import {
    collection,
    doc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    Timestamp,
} from 'firebase/firestore'
import { firestore } from '../config/firebase'
import {
    Notification,
    NotificationFilters,
    NotificationUpdater,
} from '../../../shared/types/notification.types'
import { COLLECTIONS } from '../../../shared/constants/collections'

interface IUseNotificationReturn {
    edit: (id: string, data: NotificationUpdater) => Promise<void>
    remove: (id: string) => Promise<void>
    list: (filters: NotificationFilters) => Promise<Notification[]>
}

const COLLECTION = COLLECTIONS.NOTIFICATION

const convertDoc = (id: string, data: Record<string, unknown>): Notification => ({
    id,
    ...data,
    createdAt: data.createdAt instanceof Timestamp
        ? data.createdAt.toDate()
        : new Date(data.createdAt as string),
    ...(data.readAt ? {
        readAt: data.readAt instanceof Timestamp
            ? data.readAt.toDate()
            : new Date(data.readAt as string),
    } : {}),
} as Notification)

const useNotification = (): IUseNotificationReturn => {

    /**
     * Aktualizuje stav notifikace — primárně pro nastavení read + readAt.
     * U shared notifikace tím zakryje notifikaci pro všechny uživatele.
     * @param id   - Firestore document ID
     * @param data - { read, readAt?, readBy? }
     */
    const edit = async (id: string, data: NotificationUpdater): Promise<void> => {
        await updateDoc(doc(firestore, COLLECTION, id), data as Record<string, unknown>)
    }

    const remove = async (id: string): Promise<void> => {
        await deleteDoc(doc(firestore, COLLECTION, id))
    }

    /**
     * Načte notifikace dle filtrů. Paralelně spouští více Firestore dotazů
     * (individuální + shared dle garáže / role / broadcast) a výsledky sloučí.
     *
     * @example
     * // Načtení všech nepřečtených notifikací přihlášeného uživatele:
     * list({ userUid, garageUid, role, read: false })
     */
    const list = async (filters: NotificationFilters): Promise<Notification[]> => {
        const baseConstraints = []

        if (filters.read !== undefined) {
            baseConstraints.push(where('read', '==', filters.read))
        }
        if (filters.type) {
            baseConstraints.push(where('type', '==', filters.type))
        }

        const queries = []

        // 1. Individuální notifikace pro konkrétního uživatele
        if (filters.userUid) {
            queries.push(getDocs(query(
                collection(firestore, COLLECTION),
                where('recipient.userUid', '==', filters.userUid),
                ...baseConstraints,
                orderBy('createdAt', 'desc'),
            )))
        }

        // 2. Shared notifikace cílené na garáž
        if (filters.garageUid) {
            queries.push(getDocs(query(
                collection(firestore, COLLECTION),
                where('recipient.garageUid', '==', filters.garageUid),
                ...baseConstraints,
                orderBy('createdAt', 'desc'),
            )))
        }

        // 3. Shared notifikace cílené na konkrétní servisní středisko (dealera)
        if (filters.dealerId) {
            queries.push(getDocs(query(
                collection(firestore, COLLECTION),
                where('recipient.dealerId', '==', filters.dealerId),
                ...baseConstraints,
                orderBy('createdAt', 'desc'),
            )))
        }

        // 4. Shared notifikace cílené na roli
        if (filters.role) {
            queries.push(getDocs(query(
                collection(firestore, COLLECTION),
                where('recipient.role', '==', filters.role),
                ...baseConstraints,
                orderBy('createdAt', 'desc'),
            )))
        }

        const snapshots = await Promise.all(queries)
        const seen = new Set<string>()
        const results: Notification[] = []

        for (const snapshot of snapshots) {
            for (const d of snapshot.docs) {
                if (!seen.has(d.id)) {
                    seen.add(d.id)
                    results.push(convertDoc(d.id, d.data()))
                }
            }
        }

        return results.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    }

    return { edit, remove, list }
}

export default useNotification
